import React from 'react';

interface CockpitDashboardBackgroundProps {
  activeRoleKey: string;
  theme: 'dark' | 'light' | 'purple';
  onPrimaryActionClick: () => void;
  onOpenTransitionsPanel: () => void;
  onOpenTelemetryClick?: () => void;
  children?: React.ReactNode;
}

export const ROLE_DEFINITIONS = {
  administrador: {
    name: "Administrador",
    environment: "Ambiente do Administrador",
    eyebrow: "Controle global",
    title: 'Central do <span class="text-white/50">Administrador</span>',
    description: "Controle completo da plataforma, ambientes, permissões, integrações, auditoria, segurança e operação global de toda a rede educacional.",
    primaryAction: "Abrir central administrativa",
    stats: [
      { value: "8", label: "ambientes gerenciados" },
      { value: "100%", label: "serviços monitorados" }
    ],
    primary: "#9b6cff",
    secondary: "#3de5ff",
    backgrounds: ["from-[#0b0928]", "via-[#21145b]", "to-[#3d2a91]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6z" />
        <circle cx="12" cy="11" r="2.5" />
        <path d="M8.5 17a3.5 3.5 0 0 1 7 0" />
      </svg>
    )
  },
  prefeito: {
    name: "Prefeito",
    environment: "Ambiente do Prefeito",
    eyebrow: "Gestão municipal",
    title: 'Gabinete do <span class="text-white/50">Prefeito</span>',
    description: "Visão estratégica da rede municipal, acompanhamento de indicadores, decisões executivas e comunicação direta com toda a comunidade escolar.",
    primaryAction: "Abrir painel executivo",
    stats: [
      { value: "42", label: "escolas acompanhadas" },
      { value: "98,4%", label: "operações estáveis" }
    ],
    primary: "#d8ad54",
    secondary: "#c78238",
    backgrounds: ["from-[#1a0f08]", "via-[#3d2510]", "to-[#6b4420]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M5 21V10h14v11m-16-11 9-6 9 6M9 21v-7h6v7M8 10v7M16 10v7" />
      </svg>
    )
  },
  diretor: {
    name: "Diretor",
    environment: "Ambiente do Diretor",
    eyebrow: "Direção escolar",
    title: 'Direção <span class="text-white/50">Escolar</span>',
    description: "Controle institucional da escola, acompanhamento de equipes, desempenho pedagógico, segurança e operação diária.",
    primaryAction: "Abrir gestão escolar",
    stats: [
      { value: "1.284", label: "alunos acompanhados" },
      { value: "93%", label: "metas em andamento" }
    ],
    primary: "#5971ff",
    secondary: "#3ddaff",
    backgrounds: ["from-[#07152f]", "via-[#122c67]", "to-[#2357a8]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6z" />
        <path d="m7 11 5-3 5 3-5 3z" />
        <path d="M9 13v3c2 1.5 4 1.5 6 0v-3" />
      </svg>
    )
  },
  coordenador: {
    name: "Coordenador",
    environment: "Ambiente do Coordenador",
    eyebrow: "Coordenação pedagógica",
    title: 'Coordenação <span class="text-white/50">Pedagógica</span>',
    description: "Organização pedagógica, conexão entre professores, acompanhamento de turmas e evolução dos planos de ensino.",
    primaryAction: "Abrir coordenação",
    stats: [
      { value: "38", label: "turmas coordenadas" },
      { value: "156", label: "planos acompanhados" }
    ],
    primary: "#e879f9",
    secondary: "#f9a8d4",
    backgrounds: ["from-[#1a0820]", "via-[#4a1858]", "to-[#8b2d6b]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="12" r="2.5" />
        <circle cx="18" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <path d="m8.4 10.8 7.2-3.6M8.4 13.2l7.2 3.6M12 5v14" />
      </svg>
    )
  },
  secretaria: {
    name: "Secretaria",
    environment: "Ambiente da Secretaria",
    eyebrow: "Operação administrativa",
    title: 'Secretaria <span class="text-white/50">Escolar</span>',
    description: "Matrículas, documentação, registros acadêmicos, arquivos oficiais e atendimento organizado às famílias.",
    primaryAction: "Abrir secretaria",
    stats: [
      { value: "3.902", label: "documentos válidos" },
      { value: "17 min", label: "tempo médio de resposta" }
    ],
    primary: "#ff7785",
    secondary: "#ffbf5b",
    backgrounds: ["from-[#2d0c1c]", "via-[#612139]", "to-[#9a4936]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2h8l4 4v16H6z" />
        <path d="M14 2v5h5M9 11h6M9 15h6" />
        <circle cx="15.5" cy="18.5" r="2.5" />
        <path d="m14.4 18.5.8.8 1.5-1.6" />
      </svg>
    )
  },
  professor: {
    name: "Professor",
    environment: "Ambiente do Professor",
    eyebrow: "Sala do professor",
    title: 'Sala do <span class="text-white/50">Professor</span>',
    description: "Planejamento de aulas, chamada, conteúdos, avaliações e comunicação direta com alunos, famílias e coordenação.",
    primaryAction: "Abrir sala do professor",
    stats: [
      { value: "6", label: "turmas ativas" },
      { value: "24", label: "atividades planejadas" }
    ],
    primary: "#58e19d",
    secondary: "#49c3ff",
    backgrounds: ["from-[#041f12]", "via-[#0a4028]", "to-[#127040]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m2 9 10-5 10 5-10 5z" />
        <path d="M6 11v5c4 3 8 3 12 0v-5M20 9v6M4 20h7m-4-3 3 3-3 3" />
      </svg>
    )
  },
  responsavel: {
    name: "Responsável",
    environment: "Ambiente do Responsável",
    eyebrow: "Portal da família",
    title: 'Portal da <span class="text-white/50">Família</span>',
    description: "Acompanhamento da vida escolar, frequência, comunicados, autorizações e contato seguro com a escola.",
    primaryAction: "Abrir portal da família",
    stats: [
      { value: "4", label: "dependentes vinculados" },
      { value: "12", label: "novos comunicados" }
    ],
    primary: "#ff66a9",
    secondary: "#ffab7a",
    backgrounds: ["from-[#2c0b28]", "via-[#61204e]", "to-[#963f61]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 21V9l8-6 8 6v12M8 21v-7h8v7" />
        <path d="M12 12s-3-1.7-3-4a2 2 0 0 1 3-1.4A2 2 0 0 1 15 8c0 2.3-3 4-3 4z" />
      </svg>
    )
  },
  aluno: {
    name: "Aluno",
    environment: "Ambiente do Aluno",
    eyebrow: "Experiência de aprendizagem",
    title: 'Espaço do <span class="text-white/50">Aluno</span>',
    description: "Rotina escolar, atividades, conteúdos, conquistas, comunicação e acesso simples a tudo que precisa aprender.",
    primaryAction: "Entrar no espaço do aluno",
    stats: [
      { value: "8", label: "atividades disponíveis" },
      { value: "92%", label: "progresso semanal" }
    ],
    primary: "#ff8c42",
    secondary: "#ff6b9d",
    backgrounds: ["from-[#1a0c08]", "via-[#3d1a20]", "to-[#6b2f45]"],
    icon: (
      <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2.5" width="14" height="19" rx="2" />
        <path d="M8 7h8M9 18h6m-7-7 4-2 4 2-4 2z" />
      </svg>
    )
  }
};

export const CockpitDashboardBackground: React.FC<CockpitDashboardBackgroundProps> = ({
  activeRoleKey,
  theme,
  onPrimaryActionClick,
  onOpenTransitionsPanel,
  onOpenTelemetryClick,
  children
}) => {
  const role = ROLE_DEFINITIONS[activeRoleKey.toLowerCase() as keyof typeof ROLE_DEFINITIONS] || ROLE_DEFINITIONS.administrador;

  // Custom styling dynamically injected for rotation and floating animations
  const styles = `
    @keyframes orbitSpin {
      to { transform: rotate(360deg); }
    }
    @keyframes shieldFloat {
      0%, 100% { transform: translateY(0) rotate(-0.5deg); }
      50% { transform: translateY(-12px) rotate(0.5deg); }
    }
    @keyframes ltr-pop {
      0% { transform: scale(0.3) translateY(20px); opacity: 0; }
      100% { transform: scale(1) translateY(0); opacity: 1; }
    }
    @keyframes acm-fadeInUp {
      from { transform: translateY(15px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes line-grow {
      from { width: 0%; left: 50%; transform: translateX(-50%); }
      to { width: 100%; left: 0%; transform: translateX(0%); }
    }
    .animate-orbit-spin {
      animation: orbitSpin 28s linear infinite;
    }
    .animate-orbit-spin-reverse {
      animation: orbitSpin 18s linear infinite reverse;
    }
    .animate-shield-float {
      animation: shieldFloat 5s ease-in-out infinite;
    }
  `;

  // Custom clip path for premium shield geometry
  const shieldClipPath = 'polygon(50% 0, 91% 14%, 91% 58%, 82% 75%, 67% 89%, 50% 100%, 33% 89%, 18% 75%, 9% 58%, 9% 14%)';

  return (
    <div className="absolute inset-0 z-0 flex flex-col p-8 pb-20 justify-between overflow-hidden font-sans select-none pointer-events-none">
      <style>{styles}</style>

      {/* Brand Topbar - Ocultado conforme solicitado */}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0 py-6 pt-16 gap-5 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start flex-shrink-0">
          {/* Left Side: Hero Details */}
          <article className="text-left max-w-xl pl-6 pointer-events-auto">
            <div className="flex items-center gap-3 text-[9px] font-extrabold uppercase tracking-widest text-white/60 mb-3">
              <span 
                className="w-8 h-[1px]"
                style={{
                  background: `linear-gradient(90deg, ${role.primary}, transparent)`,
                  boxShadow: `0 0 10px ${role.primary}`,
                }}
              />
              {role.eyebrow}
            </div>

            <h1 
              className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[0.95] tracking-tight mb-4 text-balance"
              dangerouslySetInnerHTML={{ __html: role.title }}
            />

            <p className="text-[12px] font-medium leading-relaxed text-white/70 mb-6">
              Central de Simulação em Tempo Real.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={onPrimaryActionClick}
                className="h-10 px-5 rounded-xl border border-white/10 text-white font-bold text-[10px] uppercase tracking-wider cursor-pointer active:scale-95 transition-all shadow-xl"
                style={{
                  background: `linear-gradient(135deg, ${role.primary}e6 0%, ${role.secondary}cc 100%)`,
                  boxShadow: `0 8px 24px ${role.primary}2c`,
                }}
              >
                {role.primaryAction}
              </button>

              <button
                onClick={onOpenTelemetryClick}
                className="h-10 px-5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white font-bold text-[10px] uppercase tracking-wider cursor-pointer active:scale-95 transition-all"
              >
                Abrir Telemetria e Simulação
              </button>
            </div>
          </article>

          {/* Right Side: Logo Stage (sempre) */}
          <div className="flex items-center justify-center w-full relative z-[2] pointer-events-none select-none">
            <div className="relative flex flex-col items-center justify-center min-h-[220px] w-full">
              <div className="absolute w-[280px] h-[280px] rounded-full border border-white/5 animate-orbit-spin" />
              <div className="absolute w-[220px] h-[220px] rounded-full border border-dashed border-white/10 animate-orbit-spin-reverse" />
              <div className="relative w-36 h-36 flex items-center justify-center animate-shield-float">
                <div className="absolute inset-0 opacity-15 blur-[30px] rounded-full" style={{ background: role.primary }} />
                <svg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block relative z-10">
                  <defs>
                    <linearGradient id={`ac-shield-outer-${activeRoleKey}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#7c3aed" />
                    </linearGradient>
                    <linearGradient id={`ac-shield-inner-${activeRoleKey}`} x1="0" y1="0" x2="0.3" y2="1">
                      <stop offset="0%" stopColor="#6d28d9" />
                      <stop offset="100%" stopColor="#4c1d95" />
                    </linearGradient>
                    <linearGradient id={`ac-cap-${activeRoleKey}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffffff" />
                      <stop offset="100%" stopColor="#e2e8f0" />
                    </linearGradient>
                  </defs>
                  <path d="M 128 12 Q 172 20 216 40 L 216 136 C 216 190 176 226 128 244 C 80 226 40 190 40 136 L 40 40 Q 84 20 128 12 Z" fill={`url(#ac-shield-outer-${activeRoleKey})`} />
                  <path d="M 128 28 Q 166 36 194 52 L 194 136 C 194 176 164 206 128 222 C 92 206 62 176 62 136 L 62 52 Q 90 36 128 28 Z" fill={`url(#ac-shield-inner-${activeRoleKey})`} />
                  <path d="M128 86 L174 104 L128 122 L82 104 Z" fill={`url(#ac-cap-${activeRoleKey})`} />
                  <path d="M96 114 L96 142 C96 150 128 156 128 156 C128 156 160 150 160 142 L160 114 L128 126 Z" fill={`url(#ac-cap-${activeRoleKey})`} />
                  <path d="M82 106 L82 144" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" />
                  <path d="M78 144 L86 144 L84 156 L80 156 Z" fill="#ffffff" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo abaixo dos botões (ex.: cards de escolas do Prefeito) */}
        {children && (
          <div className="flex-1 min-h-0 overflow-y-auto pl-6 pr-2 pointer-events-auto pb-4">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
